import {html} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";
import {when} from "lit/directives/when.js";
import {UuidHelper} from "../../../helpers/uuid.helper";
import {AbstractLitInputComponent} from "../../abstract-lit-input.component";
import type {LinkTileIcon} from "../../../domain/entities/link-tile-icon.const";

/**
 * Icon-swatch grid picker — same interaction pattern as `ha-input-color`'s
 * "already used colors" grid (click a swatch, `value`/`value-changed`
 * carries its `id`), for a caller-supplied list of Font Awesome icons.
 *
 * Renders the actual Unicode glyph character (`String.fromCodePoint`) on a
 * `font-family: "Font Awesome 6 Sharp"` element rather than a static
 * `::before { content: "\fXXX" }` rule per icon: `items` is data, any entry
 * can appear in any instance, so there is no fixed selector a per-icon CSS
 * rule could hang off (unlike the house convention for a single FIXED icon
 * usage, e.g. ha-mode-switch's per-route rules in todo_sidebar.css).
 */
@customElement(`ha-input-icon`)
export class HaInputIconComponent extends AbstractLitInputComponent<string> {

    @property({type: Array})
    public items: readonly LinkTileIcon[] = [];

    private _id = UuidHelper.generateV7();

    protected render(): unknown {
        return html`
            ${when(this.label, () => html`
                <label for=${this._id}>${this.label}</label>
            `)}
            <div class="wrapper">
                <ul class="icon-choices" id=${this._id}>
                    ${repeat(this.items, item => item.id, item => html`
                        <li class=${classMap({'icon-choices__item': true, selected: this.value === item.id})}
                            title=${item.label}
                            @click=${() => this._onChoose(item)}
                        ><i class="icon-choices__glyph">${String.fromCodePoint(parseInt(item.codepoint, 16))}</i>
                        </li>
                    `)}
                </ul>
            </div>
            ${this._renderHelperText()}
            ${this._renderErrors()}
        `;
    }

    private _onChoose(item: LinkTileIcon): void {
        if (this.readonly) {
            return;
        }

        // Same value clicked again → deselect (an icon is optional).
        this.value = this.value === item.id ? null : item.id;
        this.fire(this.value);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ha-input-icon': HaInputIconComponent;
    }
}
